import { styled } from '@mui/system';
import { Button } from '@mui/material';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import { useState, useEffect } from 'react';
import UserProfileImage from '../UserProfileImage';
import ActionDialog from '../../../../components/shared/Dialog/ActionDialog';
import LinkToListDialog from '../../../../components/shared/Dialog/LinkToListDialog';
import { useFollowUserMutation, useUnfollowUserMutation } from '../../../../store/api/userApi';
import UserFollowBtn from '../../../../components/shared/Button/UserFollowBtn';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../store/auth/authSlice';
import { DEFAULT_AVATAR } from '../../../../utils/constant';
import {
  PopupNotification,
  toastUnfollow,
  toastFollow,
} from '../../../../components/shared/PopupNotification/PopupNotification';
import { useContext } from 'react';
import { AppContext } from '../../../../context/ContextProvider';

const UserProfileContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
});

const UserDetail = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const PersonalProfile = styled('div')({
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  marginTop: '26px',
});

const UserProfileName = styled('div')(({ theme }) => ({
  height: '25px',
  lineHeight: '25px',
  fontFamily: 'Fjalla One',
  fontWeight: 400,
  fontSize: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '17px',
    lineHeight: '21px',
    height: '21px',
  },
}));

const UserProfileInf = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const UserProfileInfItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: '14px',
  marginLeft: '7px',
  lineHeight: '21px',
  fontfamily: 'Poppins',
});

const ProfileItemLabel = styled('span')({
  fontSize: '14px',
  fontFamily: 'Raleway',
});

const ProfileItem = styled('div')({
  display: 'flex',
  marginTop: '18px',
  marginLeft: '10px',
  alignItems: 'center',
  '& svg': {
    fontSize: '23px',
    color: '#AB9178',
  },
});

const ItemNumber = styled('span')({
  lineHeight: '27px',
  fontFamily: 'Poppins',
  fontWeight: 'bold',
  paddingRight: '5px',
});

const UserDescription = styled('div')(({ theme }) => ({
  textAlign: 'center',
  fontFamily: 'Raleway',
  fontSize: '14px',
  margin: '20px auto',
  fontweight: 400,
  [theme.breakpoints.down('sm')]: {
    margin: '17px auto 20px',
    lineHeight: '16px',
  },
}));

const EditButton = styled('a')(({ theme }) => ({
  color: '#000000',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '19px',
  textDecoration: 'none',
  textTransform: 'capitalize',
  display: 'flex',
  alignItems: 'center',
  marginLeft: '20px',
  border: 'none',
  '& svg': {
    marginRight: '3px',
    height: '18px',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
    lineHeight: '18px',
    '& svg': {
      height: '18px',
    },
  },
}));

const UserProfile = (props) => {
  const currentUserId = useSelector(selectUser);
  const [open, setOpen] = useState(false);
  const [followUser] = useFollowUserMutation();
  const [unfollowUser] = useUnfollowUserMutation();
  const { isShow, setIsShow, isLogin, setIsLogin } = useContext(AppContext);

  const iscurrentUserFollow = props.followers.some((e) => {
    if (e._id === currentUserId) {
      return true;
    } else return false;
  });

  const handleFollowBtnClick = async () => {
    if (iscurrentUserFollow) {
      setOpen(true);
    } else {
      const id = props._id;
      await followUser({ id, userId: currentUserId });
      setOpen(false);
      toastFollow(props.userName);
    }
  };

  const closeThePopUpWindow = () => {
    setOpen(false);
  };

  const unfollowTheUser = async () => {
    const id = props._id;

    await unfollowUser({ id, userId: currentUserId });
    setOpen(false);
    toastUnfollow(props.userName);
  };

  return (
    <UserProfileContainer>
      <UserProfileImage img={props.avatar || DEFAULT_AVATAR} alt={props.userName} />
      <PersonalProfile>
        <UserProfileName> {props.userName} </UserProfileName>
        {currentUserId && isLogin ? (
          currentUserId === props._id ? (
            <EditButton href="/user/setting">
              <SettingsIcon />
              edit profile
            </EditButton>
          ) : (
            <>
              <UserFollowBtn
                followState={iscurrentUserFollow}
                handleClick={handleFollowBtnClick}
                currentUser={currentUserId}
                isLogin={isLogin}
              />
              <PopupNotification />
              <ActionDialog
                img={props.avatar}
                name={props.userName}
                onClose={closeThePopUpWindow}
                open={open}
                variant="user"
                action="unfollow"
                actionHandler={unfollowTheUser}
                cancelHandler={closeThePopUpWindow}
              />
            </>
          )
        ) : null}
      </PersonalProfile>

      <UserDetail>
        <UserProfileInf>
          <ProfileItem>
            <ImportContactsIcon />
            <UserProfileInfItem>
              <ItemNumber>{props.postedNotes.length} </ItemNumber>
              <ProfileItemLabel>Notes</ProfileItemLabel>
            </UserProfileInfItem>
          </ProfileItem>

          <ProfileItem>
            <UserProfileInfItem>
              <LinkToListDialog title="followers" userList={props.followers}>
                <SentimentSatisfiedAltIcon />
                <ItemNumber>{props.followers.length} </ItemNumber>
                followers
              </LinkToListDialog>
            </UserProfileInfItem>
          </ProfileItem>
          <ProfileItem>
            <UserProfileInfItem>
              <LinkToListDialog
                title="following"
                userList={props.followings}
                currentUser={currentUserId}
              >
                <SupervisorAccountIcon />
                <ItemNumber>{props.followings.length} </ItemNumber>
                following
              </LinkToListDialog>
            </UserProfileInfItem>
          </ProfileItem>
        </UserProfileInf>
        <UserDescription>{props.personalIntro}</UserDescription>
      </UserDetail>
    </UserProfileContainer>
  );
};

export default UserProfile;
